import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "./Firebase/Config";
import Sidebar from './Components/SideBar/SideBar';
import Auth from "./Pages/Auth";
import Dashboard from "./Pages/HomePage";
import { ThemeProvider, createTheme } from '@mui/material/styles';
import DecaissementsPage from "./Pages/Caisse/Decaissement"
import EncaissementsPage from "./Pages/Caisse/Encaissement";
import VaultPage from "./Pages/Caisse/VaultPage";
import CaisseTransactions from "./Pages/Caisse/CaisseTransaction"
import RHPage from "./Pages/RH/RH";
import WorkerDetails from "./Pages/RH/RHWorker";
import FournisseurPage from "./Pages/Fournisseur";
import PdfTemplate from "./Pages/FactureGeneration";
import Analytics from "./Pages/Analytics"
import PDFDownloadPage from "./Pages/Declaration";
import PartenairesPage from "./Pages/Partenaire";
import StockScreen from "./Pages/FoodCost/Stock";
import FoodCostPage from "./Pages/FoodCost/FoodCost";
import DailyServingsPage from "./Pages/FoodCost/DailyServings";
import DailyServingsAnalyticsPage from "./Pages/FoodCost/DailyServingsAnalyticsPage";
import UserProfile from "./Pages/Profile";
import SupportPage from "./Pages/Support/SupportPage";
import MainAppBar from "./Components/SideBar/AppBar";
import MarketplaceComingSoon from "./Pages/MarketPlace";
import IntegrationCaisseComingSoon from "./Pages/IntegrationCaisse";
import { Box } from '@mui/material';
import InventairePage from "./Pages/FoodCost/Inventaire";
import RestaurantPage from "./Pages/RestaurantPage";
import AchatStockScreen from "./Pages/FoodCost/Achat";
import ForgotPassword from "./Pages/ForgetPassword";
import ReclamationPage from "./Pages/Recmlamation";
import { RestaurantProvider, useRestaurant } from './Context/RestaurantContext';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#ff4081',
    },
  },
  typography: {
    // Add your custom typography options here
  },
});

const App = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <ThemeProvider theme={theme}>
      <RestaurantProvider>
        <Router>
          <MainContent 
            currentUser={currentUser} 
            sidebarOpen={sidebarOpen}
            onSidebarToggle={handleSidebarToggle}
          />
        </Router>
      </RestaurantProvider>
    </ThemeProvider>
  );
};

const ProtectedRoute = ({ currentUser, children }) => {
  return currentUser ? children : <Navigate to="/" />;
};

const MainContent = ({ currentUser, sidebarOpen, onSidebarToggle }) => {
  const location = useLocation();
  const { refreshTrigger } = useRestaurant();
  const showAppBarAndSidebar = currentUser && location.pathname !== "/AddResto";
  const sidebarWidth = 300;

  // Force re-render of all components when refreshTrigger changes
  useEffect(() => {
    console.log('Restaurant changed, refreshing all components...');
  }, [refreshTrigger]);

  return (
    <Box sx={{ 
      display: 'flex', 
      minHeight: '100vh',
      bgcolor: '#F5F7FA'
    }}>
      {/* Sidebar - Fixed position */}
      {showAppBarAndSidebar && (
        <Box
          sx={{
            width: sidebarOpen ? sidebarWidth : 0,
            flexShrink: 0,
            position: 'fixed',
            height: '100vh',
            left: 0,
            top: 0,
            zIndex: 1200,
            transition: 'width 0.3s ease',
            overflow: 'hidden'
          }}
        >
          <Sidebar onToggle={onSidebarToggle} />
        </Box>
      )}

      {/* Main Content Area */}
      <Box
        sx={{
          flexGrow: 1,
          ml: showAppBarAndSidebar ? (sidebarOpen ? `${sidebarWidth}px` : 0) : 0,
          transition: 'margin-left 0.3s ease',
          width: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* App Bar */}
        {showAppBarAndSidebar && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              right: 0,
              left: sidebarOpen ? `${sidebarWidth}px` : 0,
              zIndex: 1100,
              transition: 'left 0.3s ease',
              height: 64
            }}
          >
            <MainAppBar onMenuClick={onSidebarToggle} user={currentUser} />
          </Box>
        )}

        {/* Page Content */}
        <Box
          sx={{
            flexGrow: 1,
            mt: showAppBarAndSidebar ? '64px' : 0,
            p: showAppBarAndSidebar ? 3 : 0,
            bgcolor: '#F5F7FA',
            minHeight: '100vh'
          }}
        >
          <Routes>
            <Route
              path="/ForgetPassword"
              element={<ForgotPassword />}
            />
            <Route path="/" element={currentUser ? <Navigate to="/resto" /> : <Auth />} />

            <Route
              path="/Home"
              element={<ProtectedRoute currentUser={currentUser}><Dashboard /></ProtectedRoute>}
            />
            
            <Route
              path="/Decaissement"
              element={<ProtectedRoute currentUser={currentUser}><DecaissementsPage /></ProtectedRoute>}
            />
            
            <Route
              path="/MarketPlace"
              element={<ProtectedRoute currentUser={currentUser}><MarketplaceComingSoon /></ProtectedRoute>}
            />
            
            <Route
              path="/IntegrationCaisse"
              element={<ProtectedRoute currentUser={currentUser}><IntegrationCaisseComingSoon /></ProtectedRoute>}
            />
            <Route
              path="/ReclamationPage"
              element={<ProtectedRoute currentUser={currentUser}><ReclamationPage /></ProtectedRoute>}
            />
            
            <Route
              path="/Encaissement"
              element={<ProtectedRoute currentUser={currentUser}><EncaissementsPage /></ProtectedRoute>}
            />
            
            <Route
              path="/Vault"
              element={<ProtectedRoute currentUser={currentUser}><VaultPage /></ProtectedRoute>}
            />
            
            <Route
              path="/AllVault"
              element={<ProtectedRoute currentUser={currentUser}><CaisseTransactions /></ProtectedRoute>}
            />

            <Route
              path="/RH"
              element={<ProtectedRoute currentUser={currentUser}><RHPage /></ProtectedRoute>}
            />

            <Route
              path="/worker/:workerId"
              element={<ProtectedRoute currentUser={currentUser}><WorkerDetails /></ProtectedRoute>}
            />
            
            <Route
              path="/DailyServings"
              element={<ProtectedRoute currentUser={currentUser}><DailyServingsPage /></ProtectedRoute>}
            />
            
            <Route
              path="/DailyServingsAnalytics"
              element={<ProtectedRoute currentUser={currentUser}><DailyServingsAnalyticsPage /></ProtectedRoute>}
            />
            
            <Route
              path="/fournisseurs"
              element={<ProtectedRoute currentUser={currentUser}><FournisseurPage /></ProtectedRoute>}
            />
            
            <Route
              path="/Facture"
              element={<ProtectedRoute currentUser={currentUser}><PdfTemplate /></ProtectedRoute>}
            />
            
            <Route
              path="/Formulaire"
              element={<ProtectedRoute currentUser={currentUser}><PDFDownloadPage /></ProtectedRoute>}
            />
            
            <Route
              path="/Partenaire"
              element={<ProtectedRoute currentUser={currentUser}><PartenairesPage /></ProtectedRoute>}
            />
            
            <Route
              path="/Stock"
              element={<ProtectedRoute currentUser={currentUser}><StockScreen /></ProtectedRoute>}
            />
            <Route
              path="/Inventaire"
              element={<ProtectedRoute currentUser={currentUser}><InventairePage /></ProtectedRoute>}
            />
            <Route
              path="/resto"
              element={<ProtectedRoute currentUser={currentUser}><RestaurantPage /></ProtectedRoute>}
            />
            <Route
              path="/Achat"
              element={<ProtectedRoute currentUser={currentUser}><AchatStockScreen /></ProtectedRoute>}
            />
            
            <Route
              path="/foodcost"
              element={<ProtectedRoute currentUser={currentUser}><FoodCostPage /></ProtectedRoute>}
            />
            
            <Route
              path="/Profile"
              element={<ProtectedRoute currentUser={currentUser}><UserProfile /></ProtectedRoute>}
            />
            
            <Route
              path="/Support"
              element={<ProtectedRoute currentUser={currentUser}><SupportPage /></ProtectedRoute>}
            />
            
            <Route
              path="/Analytics"
              element={<ProtectedRoute currentUser={currentUser}><Analytics /></ProtectedRoute>}
            />
          </Routes>
        </Box>
      </Box>
    </Box>
  );
};

export default App;